import { useCallback, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useCurrentUser } from "src/business-hooks/useCurrentUser";
import { useSearchParamState } from "src/hooks/useSearchParams";
import { useQueryEnvs } from "src/queries/env/env.query";
import { useQuerySystemSettings } from "src/queries/platform/system.query";
import { useGlobalLocalContext } from "src/storage/localStorage";
import {
  getIsSwitchingEnv,
  setIsSwitchingEnv,
  useSearchStorage,
} from "src/storage/useSearchStorage";
import { getRouteObject } from "src/utils/appRoutes/appRoutes";
import { useShallow } from "zustand/shallow";
import { SYSTEM_SETTINGS_SEARCH_PARAM } from "./components/SystemSettings/SystemSettings.constants";

const storageGetter = (state: any) => ({
  isDebugMode: state.isDebugMode,
  env: state.env,
  setEnv: state.setEnv,
});

export const useEnv = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { env_name: envFromUrl } = useParams();
  const { data: envs } = useQueryEnvs();
  const {
    isDebugMode,
    env: envFromStorage,
    setEnv: setStorageEnv,
  } = useGlobalLocalContext(useShallow(storageGetter));

  const switchEnv = useCallback(
    async (env: string) => {
      const envUrl = getRouteObject(pathname)?.getBasePath(env);

      if (envUrl) {
        // Set flag to prevent setItem from calling pushState (avoids race with navigate)
        setIsSwitchingEnv(true);

        // Update env in localStorage
        setStorageEnv(env);

        // Clear all URL search params so rehydrate reads purely from storage
        // (prevents old env's URL values from overriding new env's stored values,
        // and prevents non-storable params like "dashboard" from being carried over)
        const url = new URL(window.location.href);
        url.search = "";
        window.history.replaceState({}, "", url);

        // Rehydrate loads the new env's stored filter values (timeWindow, appTags, etc.)
        // from the per-env nested storage into Zustand state
        await useSearchStorage.persist.rehydrate();

        // Navigate then clear the flag — defer so the flag stays set through
        // any synchronous Zustand subscriber renders triggered by navigate()
        void Promise.resolve(navigate(envUrl)).finally(() =>
          setIsSwitchingEnv(false)
        );
      }
    },
    [pathname, navigate, setStorageEnv]
  );

  // Sync stored env with URL env on direct navigation, or initialize on first visit.
  // After setting env, rehydrate useSearchStorage so hashStorage.getItem picks up
  // the correct env and initializes per-env nested storage. Without this, if
  // useSearchStorage hydrated before env was set (via useCommonQueryParams in makeQuery),
  // setItem silently drops writes because getCurrentEnv() returns "".
  useEffect(() => {
    if (!envs?.length) return;

    // switchEnv stores the next env before navigate(). Snapping storage back
    // to the URL env cancels that rehydrate. A synchronous navigation then
    // writes the previous env's time window onto the new env's URL, and a
    // valid param is never replaced.
    if (getIsSwitchingEnv()) return;

    if (!envFromStorage) {
      // First visit: default to env with the most pipelines (envs are sorted by cnt desc)
      setStorageEnv(envFromUrl || envs[0].env);
      void useSearchStorage.persist.rehydrate();
    } else if (envFromUrl && envFromUrl !== envFromStorage) {
      // Direct URL navigation to a different env (e.g. page.goto("/envs/demo/"))
      // — keep stored env in sync so per-env storage writes go to the correct slot
      setStorageEnv(envFromUrl);
      void useSearchStorage.persist.rehydrate();
    }
  }, [envs, envFromStorage, setStorageEnv, envFromUrl]);

  return {
    env: envFromUrl || envFromStorage,
    switchEnv,
    isDebugMode,
  };
};

/**
 * system settings hooks
 */
export const useSystemSettings = () => {
  const user = useCurrentUser();
  const systemSettingsQuery = useQuerySystemSettings({
    enabled: !!user?.is_tenant_admin,
  });
  const [value, setValue] = useSearchParamState(SYSTEM_SETTINGS_SEARCH_PARAM);

  const toggleSystemSettings = () => {
    setValue("true");
  };

  return {
    systemSettingsQuery,
    isSystemSettingsOpen: !!value,
    toggleSystemSettings,
  };
};
